import { Box, Typography } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import dayjs from "dayjs"
import React from "react"

import StyledChip from "@/components/common/Chip/Chip"
import FileChip from "@/components/upload/components/display/FileChip"
import { DATE_FORMAT } from "@/constants/formats"
import type { CompanyCertification, SignedUrl } from "@/graphql/codegen/graphql"
import { HISTORICAL_AMOUNT_OF_CERTIFICATIONS } from "@/screens/Companies/edit/components/certification/constants"
import type { GroupedByLocation } from "@/screens/Companies/edit/components/certification/types"
import { getCertificationStatus } from "@/screens/Companies/edit/components/certification/utils"
import { CertificationStatusColorMap } from "@/screens/Companies/edit/components/certification/utils/certificationStatusMap"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

const useHistoricCertificationColumns = ({ fileData }: { fileData: SignedUrl[] }): ColumnDef<GroupedByLocation>[] => {
  const renderCertificationCell = (companyCertification: CompanyCertification) => {
    const data = fileData.find((el) => el.file?.referenceId === companyCertification.companyCertificationId)
    const certificationStatus = getCertificationStatus(companyCertification.expirationDate!)
    return (
      <Box className='flex gap-2' key={companyCertification.companyCertificationId}>
        <StyledChip
          label={dayjs(companyCertification.expirationDate).format(DATE_FORMAT.US)}
          classes={{
            colorDefault: CertificationStatusColorMap.get(certificationStatus),
          }}
          className='text-gray-900'
        />
        {data && <FileChip file={data} showFileName={false} />}
      </Box>
    )
  }

  return [
    {
      accessorKey: "location",
      header: "Location",
      cell: ({ row }) => {
        const { location } = row.original

        return (
          <Box className='flex'>
            <Typography variant='body2' color='textSecondary' className='truncate'>
              <span className='text-gray-900'>{location.name}</span> -{" "}
              {formatLocationAddress(location, AddressFormat.AddressOnly)}
            </Typography>
          </Box>
        )
      },
      size: 300,
      enableSorting: false,
    },
    {
      accessorKey: "historicCertificates",
      header: "Historic Certificates",
      size: 600,
      cell: ({ row }) => {
        const certificates = row.original?.certificates

        const formatedData = [...certificates]
          .sort((a, b) => dayjs(b.effectiveDate).diff(dayjs(a.effectiveDate)))
          .slice(0, HISTORICAL_AMOUNT_OF_CERTIFICATIONS)

        if (!formatedData?.length) return "-"

        return (
          <Box className='flex flex-wrap gap-4'>
            {formatedData?.map((certificate) => renderCertificationCell(certificate))}
          </Box>
        )
      },
      enableSorting: false,
    },
  ]
}

export default useHistoricCertificationColumns
